<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import { IS_QUICKVIEW_KEY } from '@bc/discovery/feature/recommendation/components/recommendations-carousel/constants';
import { CardProductEmits } from '../types/card-product-emits';
import type { ProductOutputData } from '../services/product-builder';
import { Card } from '@bc/discovery/ui/cards';
import CardProductNames from './card-product-names.vue';
import CardProductPrice from './card-product-price.vue';
import CardProductDiscountLabel from './card-product-discount-label.vue';
import CardProductDiscountBadges from './card-product-discount-badges.vue';
import CardProductImage from './card-product-image.vue';
import CardProductEstimatedDelivery from './card-product-estimated-delivery.vue';
import CardProductSponsoredLabel from './card-product-sponsored-label.vue';
import AddToCart from '@bc/shared/components/add-to-cart/add-to-cart.vue';
import CardAddToCart from './card-add-to-cart.vue';
import QuickAddToCart from '@monolith/legacy/components/cart/quick-add-to-cart.vue';
import type { ESProductHit } from '@bc/discovery/types';
import LikeButton from '@monolith/legacy/components/like-button.vue';
import BadgesList from '@monolith/legacy/modules/promote-engage/touchpoints/badges-list.vue';
import { AkIcon, AkRoundedButton } from '@ankorstore/design-system';
import { useBreakpoints } from '@monolith/legacy/modules/design-system-candidates';
import { CustomRouterLink } from '@bc/shared';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';
import { isTileSimplified } from '../services/tile-simplification';
import { trackProductImageSwiped, type ProductImageSwipeDirection } from '../services/tracking';
import { useAuthPopin } from '@core/composables/use-auth-popin';
import { useProductQuickView } from '@bc/shared/composables/use-product-quick-view';
import { AuthFormType } from '@monolith/legacy/types/auth-form-type';
import type Product from '@monolith/legacy/types/product';

defineOptions({
  name: 'CardProduct',
});

type ProductViewModel = ProductOutputData;

const props = defineProps<{
  product: ProductViewModel;
  qtacProduct?: Product | ESProductHit;
  pageNumber: number;
  position: number;
  canGetWholesalePrice: boolean;
  isUserAuthenticated: boolean;
  isExternalLink?: boolean;
  withDrawer?: boolean;
  hideVolumeDiscount?: boolean;
  hideEstimatedDelivery?: boolean;
}>();
defineEmits<CardProductEmits>();

const brandUuid = computed(() => props.qtacProduct?.brand?.uuid ?? props.product?.brand?.uuid);

// Feature flags are static per page load, so resolve the PRICE-2 kill-switch once
// rather than calling isTileSimplified() repeatedly across the template.
const isSimplified = isTileSimplified();

const onImageSwiped = (direction: ProductImageSwipeDirection): void => {
  trackProductImageSwiped({
    productId: props.product.id,
    position: props.position,
    pageNumber: props.pageNumber,
    direction,
  });
};

const { isAboveOrEqualLg } = useBreakpoints();

provide(IS_QUICKVIEW_KEY, computed(() => !!props.withDrawer));

const { openProductQuickView } = useProductQuickView();

const openDrawer = (): void => {
  openProductQuickView(props.product.id);
};

const quickAddToCart = ref();
const { openAuthPopin } = useAuthPopin();

// Adapter: ProductOutputData uses camelCase (unitMultiplier), ESProductHit uses snake_case (unit_multiplier).
// The shared AddToCart composable reads unit_multiplier, so we normalize here.
const addToCartProduct = computed(
  () => ({ ...props.product, unit_multiplier: props.product.unitMultiplier }) as unknown as ESProductHit
);

const useSharedAddToCart = computed(() => isEnabled(FeatureFlag.SharedAddToCart));
const isRet3605Enabled = computed(() => isEnabled('RET-3605'));

const showLegacyQuickAddToCart = computed(
  () => !useSharedAddToCart.value && props.isUserAuthenticated && props.canGetWholesalePrice && !isRet3605Enabled.value
);
const showCardAddToCart = computed(() => !useSharedAddToCart.value && isRet3605Enabled.value);

const handleCardClick = (event: Event) => {
  if (!props.isUserAuthenticated) {
    const target = event.target as HTMLElement;
    const link = target.closest('a');
    if (link) {
      event.preventDefault();
      event.stopPropagation();
      openAuthPopin(AuthFormType.Login);
    }
  }
};
</script>

<template>
  <Card
    v-if="product"
    class="card-product"
    @click.capture="handleCardClick"
  >
    <template #topLeftActions>
      <AkRoundedButton
        v-if="showLegacyQuickAddToCart"
        class="card-product__add-to-cart-button"
        @click="quickAddToCart.execute()"
      >
        <AkIcon
          symbol="cart-plus"
          size="md"
        />
      </AkRoundedButton>
    </template>

    <template #topRightActions="{ isHovered }">
      <LikeButton
        :id="product.id"
        type="product"
        data-testid="favourite-product"
        :with-tooltip="!isSimplified && isAboveOrEqualLg"
        :is-focused="isHovered"
        :disable-tracking="true"
        @like-product="$emit('likeClicked', $event)"
        @unlike-product="$emit('unlikeClicked', $event)"
      />
      <slot
        name="topRightActions"
        v-bind="{ isHovered }"
      />
    </template>

    <template #topContent="{ isHovered }">
      <div
        v-if="withDrawer"
        @click="
          openDrawer();
          $emit('clickedDrawer', $event);
        "
      >
        <CardProductImage
          :src="product.image"
          :images="product.images"
          :alt="product.name"
          :is-overlay-visible="isHovered"
          :enable-gallery="isSimplified"
          @swiped="onImageSwiped"
        />
      </div>
      <CustomRouterLink
        v-else
        :to="product.url"
        :is-external-link="isExternalLink"
        :url="product.url"
        @click="$emit('clicked', $event)"
      >
        <CardProductImage
          :src="product.image"
          :images="product.images"
          :alt="product.name"
          :is-overlay-visible="isHovered"
          :enable-gallery="isSimplified"
          @swiped="onImageSwiped"
        />
      </CustomRouterLink>
      <div class="card-product__badges">
        <BadgesList
          :class="{ 'card-product__badges--hide-on-hover': canGetWholesalePrice }"
          :badge-infos="product.badges"
        />
      </div>
    </template>

    <template #middleActions>
      <QuickAddToCart
        v-if="showLegacyQuickAddToCart"
        ref="quickAddToCart"
        :index="position"
        :page-number="pageNumber"
        :product="qtacProduct"
        data-testid="qatc-desktop"
        class="card-product__add-to-cart"
        @add-to-cart-clicked="(event) => $emit('addToCartClicked', event)"
        @product-added-to-cart="(event) => $emit('addedToCart', event)"
        @variant-added-to-cart="(event) => $emit('addedToCart', event)"
      />
    </template>

    <template #bottomContent>
      <CardProductNames
        :product="product"
        :is-external-link="isExternalLink"
        :with-drawer="withDrawer"
        :simplified="isSimplified"
        @product-clicked="withDrawer ? (openDrawer(), $emit('clickedDrawer', $event)) : $emit('clicked', $event)"
        @brand-clicked="$emit('brandClicked', $event)"
      />
      <div
        v-if="withDrawer && canGetWholesalePrice"
        class="card-product__price-link"
        :class="isSimplified ? 'card-product__price-link--bottom' : 'card-product__price-link--grow'"
        @click="
          openDrawer();
          $emit('clickedDrawer', $event);
        "
      >
        <CardProductPrice
          :product="product"
          :show-price="canGetWholesalePrice"
          :simplified="isSimplified"
        />

        <CardProductDiscountLabel
          v-if="isSimplified"
          :brand-uuid="brandUuid"
          :hide-volume-discount="hideVolumeDiscount"
          :discount-percentage="product.discountPercentage"
          :has-discounted-price="!!product.discountedPrice"
          :show-price="canGetWholesalePrice"
          :out-of-stock="product.outOfStock"
        />
        <CardProductDiscountBadges
          v-else
          :brand-uuid="brandUuid"
          :hide-volume-discount="hideVolumeDiscount"
        />

        <CardProductEstimatedDelivery
          v-if="!isSimplified && canGetWholesalePrice && !product.outOfStock && !hideEstimatedDelivery"
          :min-days="product.estimatedDelivery?.minDays"
          :max-days="product.estimatedDelivery?.maxDays"
          :is-fast-delivery="product.isFastDelivery"
        />
      </div>
      <CustomRouterLink
        v-else
        :to="product.url"
        :is-external-link="isExternalLink"
        :url="product.url"
        class="card-product__price-link"
        :class="isSimplified ? 'card-product__price-link--bottom' : 'card-product__price-link--grow'"
        @click="$emit('clicked', $event)"
      >
        <CardProductPrice
          :product="product"
          :show-price="canGetWholesalePrice"
          :simplified="isSimplified"
        />

        <CardProductDiscountLabel
          v-if="isSimplified"
          :brand-uuid="brandUuid"
          :hide-volume-discount="hideVolumeDiscount"
          :discount-percentage="product.discountPercentage"
          :has-discounted-price="!!product.discountedPrice"
          :show-price="canGetWholesalePrice"
          :out-of-stock="product.outOfStock"
        />
        <CardProductDiscountBadges
          v-else
          :brand-uuid="brandUuid"
          :hide-volume-discount="hideVolumeDiscount"
        />

        <CardProductEstimatedDelivery
          v-if="!isSimplified && canGetWholesalePrice && !product.outOfStock && !hideEstimatedDelivery"
          :min-days="product.estimatedDelivery?.minDays"
          :max-days="product.estimatedDelivery?.maxDays"
          :is-fast-delivery="product.isFastDelivery"
        />
      </CustomRouterLink>
      <CardProductSponsoredLabel v-if="product.isSponsored" />
      <div
        v-if="useSharedAddToCart"
        class="card-product__shared-atc"
      >
        <AddToCart
          :product="addToCartProduct"
          @add-to-cart-clicked="(event) => $emit('addToCartClicked', event)"
          @added-to-cart="(event) => $emit('addedToCart', event)"
          @variant-added-to-cart="(event) => $emit('addedToCart', event)"
        />
      </div>
      <CardAddToCart
        v-else-if="showCardAddToCart"
        :auto-margin-top="!isSimplified"
        :product="product"
        @add-to-cart-clicked="(event) => $emit('addToCartClicked', event)"
        @added-to-cart="(event) => $emit('addedToCart', event)"
        @variant-added-to-cart="(event) => $emit('addedToCart', event)"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.card-product {
  $root: &;

  @apply ds-cursor-pointer ds-relative;

  &__badges {
    @apply ds-absolute ds-z-[1] ds-flex ds-items-center ds-gap-1;

    bottom: 18px;
    left: 7px;

    &--hide-on-hover {
      @apply ds-opacity-100 ds-transition-opacity;

      @media (screen(lg)) {
        #{$root}:hover & {
          @apply ds-opacity-0;
        }
      }
    }
  }


  &__add-to-cart {
    @apply ds-absolute ds-invisible ds-opacity-0 ds-transition-opacity;

    left: 7px;
    right: 7px;
    bottom: 60px;

    #{$root}:hover & {
      @apply ds-opacity-100;
    }

    @media (screen(lg)) {
      @apply ds-visible;
    }
  }

  &__add-to-cart-button {
    @apply ds-block lg:ds-hidden;
  }

  &__shared-atc {
    @apply ds-w-full ds-flex ds-justify-center ds-items-center ds-px-2 ds-pb-2;
  }

  &__price-link {
    @apply ds-flex ds-flex-col;

    // Legacy tile: grow to fill, price sits directly under the name.
    &--grow {
      @apply ds-flex-1;
    }

    // PRICE-2 simplified tile: auto space above the price group so price / MSRP /
    // CTA are bottom-aligned and line up across the grid regardless of name length.
    // (The CTA drops its own margin-top: auto via :auto-margin-top so this is the
    // single spacer — otherwise the two auto margins would split the space.)
    &--bottom {
      @apply ds-mt-auto;
    }
  }

}
</style>
